import { useMemo } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  Body,
  Card,
  Caption,
  EmptyState,
  VButton,
  VField,
  VInput,
  VSelect,
  VTextarea,
} from "@/components/ui";
import {
  AssetTypeIcon,
  DEFAULT_ASSET_TYPE_LABELS,
  useAssetTypeLabels,
  useCustomAssetTypes,
} from "@/domains/assets";
import type { RequestedAssetLine } from "@/domains/requests/api/types";
import { createEmptyAssetLine } from "@/domains/requests/components/requestWizard/wizardState";

type StepAssetsProps = {
  value: ReadonlyArray<RequestedAssetLine>;
  onChange: (next: ReadonlyArray<RequestedAssetLine>) => void;
};

type AssetOption = {
  code: string;
  label: string;
};

export function StepAssets({ value, onChange }: StepAssetsProps): React.JSX.Element {
  const [builtInLabels] = useAssetTypeLabels();
  const [customTypes] = useCustomAssetTypes();

  const options = useMemo<ReadonlyArray<AssetOption>>(() => {
    const builtIn = (Object.keys(DEFAULT_ASSET_TYPE_LABELS) as Array<
      keyof typeof DEFAULT_ASSET_TYPE_LABELS
    >).map<AssetOption>((code) => ({ code, label: builtInLabels[code] }));
    const custom = customTypes.map<AssetOption>((c) => ({ code: c.code, label: c.label }));
    return [...builtIn, ...custom];
  }, [builtInLabels, customTypes]);

  const patchLine = (id: string, partial: Partial<RequestedAssetLine>): void => {
    onChange(value.map((line) => (line.id === id ? { ...line, ...partial } : line)));
  };

  const addLine = (): void => {
    onChange([...value, createEmptyAssetLine()]);
  };

  const removeLine = (id: string): void => {
    onChange(value.filter((line) => line.id !== id));
  };

  return (
    <Card padding="lg" className="space-y-4">
      <div className="space-y-1">
        <Body weight="medium">7. Welk materiaal heb je nodig?</Body>
        <Caption tone="muted">
          Voeg per materiaaltype een lijn toe. De planner gebruikt dit om beschikbare assets te
          reserveren — definitieve toewijzing gebeurt later.
        </Caption>
      </div>

      {value.length === 0 ? (
        <EmptyState
          title="Nog geen materiaal toegevoegd"
          description="Voeg minstens één lijn toe om door te gaan."
          action={
            <VButton onClick={addLine} variant="outline">
              <Plus className="size-4" aria-hidden />
              Lijn toevoegen
            </VButton>
          }
        />
      ) : (
        <div className="space-y-3">
          {value.map((line, idx) => (
            <AssetLineRow
              key={line.id}
              index={idx}
              line={line}
              options={options}
              onPatch={(partial) => patchLine(line.id, partial)}
              onRemove={() => removeLine(line.id)}
            />
          ))}

          <div>
            <VButton variant="outline" onClick={addLine}>
              <Plus className="size-4" aria-hidden />
              Lijn toevoegen
            </VButton>
          </div>
        </div>
      )}
    </Card>
  );
}

type AssetLineRowProps = {
  index: number;
  line: RequestedAssetLine;
  options: ReadonlyArray<AssetOption>;
  onPatch: (partial: Partial<RequestedAssetLine>) => void;
  onRemove: () => void;
};

function AssetLineRow({
  index,
  line,
  options,
  onPatch,
  onRemove,
}: AssetLineRowProps): React.JSX.Element {
  const typeId = `asset-type-${line.id}`;
  const qtyId = `asset-qty-${line.id}`;
  const noteId = `asset-note-${line.id}`;

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {line.assetType ? (
            <AssetTypeIcon
              type={line.assetType}
              className="size-4 text-primary-700"
            />
          ) : null}
          <Caption tone="muted">Lijn {index + 1}</Caption>
        </div>
        <VButton
          variant="ghost"
          size="sm"
          onClick={onRemove}
          aria-label={`Lijn ${index + 1} verwijderen`}
        >
          <Trash2 className="size-4" aria-hidden />
          Verwijderen
        </VButton>
      </div>

      <div className="grid gap-3 md:grid-cols-[1fr_140px]">
        <VField label="Type materiaal" htmlFor={typeId} required>
          <VSelect
            id={typeId}
            value={line.assetType}
            onChange={(e) => onPatch({ assetType: e.target.value })}
          >
            <option value="" disabled>
              Kies een type…
            </option>
            {options.map((opt) => (
              <option key={opt.code} value={opt.code}>
                {opt.label}
              </option>
            ))}
          </VSelect>
        </VField>

        <VField label="Aantal" htmlFor={qtyId} required>
          <VInput
            id={qtyId}
            type="number"
            min={1}
            value={line.quantity}
            onChange={(e) => {
              const next = Number.parseInt(e.target.value, 10);
              onPatch({ quantity: Number.isFinite(next) && next > 0 ? next : 1 });
            }}
          />
        </VField>
      </div>

      <div className="mt-3">
        <VField
          label="Toelichting (optioneel)"
          htmlFor={noteId}
          hint="Bv. specifieke kenmerken, accessoires, opstellingsvoorkeur."
        >
          <VTextarea
            id={noteId}
            rows={2}
            value={line.note ?? ""}
            onChange={(e) => onPatch({ note: e.target.value })}
          />
        </VField>
      </div>
    </div>
  );
}
